import type { PlanDisplay, PlanKey } from "./types";

/**
 * What each plan is called, what it costs, and what it includes — the copy a customer reads.
 *
 * STATIC, and shared. It lives here rather than behind `GET /plans` because it is marketing text
 * with no per-organization content: the same words for every visitor, known at build time. Fetching
 * it made the plan chooser depend on an authenticated round trip, so a signup screen — where the
 * caller is by definition not yet provisioned — could render a price and a button with no features
 * under them and no way to say why.
 *
 * Nothing is exposed by shipping it: the API already sends this exact object to the browser.
 *
 * ENTITLEMENTS ARE NOT HERE. Those stay server-side in `apps/gfgf-api/src/lib/entitlements/plans`,
 * because they are what the server ENFORCES and the client must never be the authority on. This is
 * the half a customer reads; that is the half a limit is checked against. The API's plan templates
 * import their display from here, so there is one copy of the words.
 */
export const PLAN_DISPLAY: Record<PlanKey, PlanDisplay> = {
  starter: {
    name: "Good Starter",
    amountLabel: "$0",
    unit: "/month",
    billingLabel: "Start sending gifts today!",
    badgeLabel: "Starter",
    features: [
      "1 user",
      "Send gifts from our curated house collections",
      "Over 500 highly curated social impact gifts available to send",
      "Recipients can choose a physical gift or support a charity instead",
      "Deliver your gift collections with a beautiful email that you can customize with your own branding",
      "Add a business logo to your campaigns and choose from a library of creative banners",
      "Send to 20 recipients per campaign",
      "Impact story cards shipped with every tangible gift",
      "Shipping and tax included in gift prices for predictable budgeting",
      "Basic campaign and impact reporting",
      "Email and chat support",
      "Pay upfront for gift credits",
      "Guaranteed Good — send unused gift credits to your chosen charity",
      "Credit wallet access — option to roll over unused gift credits for up to 12 months",
    ],
  },
  pro: {
    name: "Good Pro",
    amountLabel: "$95",
    unit: "/month",
    billingLabel: "Billed monthly · or $1,000 billed annually",
    badgeLabel: "Pro",
    features: [
      { divider: "Everything in Starter, plus" },
      "3 users",
      "Full suite of branding customization for gift collection landing pages — custom video display, color adjustments, and custom banner uploads",
      "Edit our existing gift collections to add or remove products, or rearrange the display order",
      "Send to up to 2,000 recipients per campaign",
      "Add branded print-on-demand merch to your gift collections from a curated catalog of products",
      "Automate gift sending per contact on specific dates for birthdays and work anniversaries",
      "Send physical gifts internationally to 30+ countries including USA, CA, UK, EU, Asia, AU & NZ",
      "Generate a universal redemption link for each campaign for easy follow-ups to your recipients",
      "Generate unique redemption links so you can send them manually through Slack or your own email provider",
      "Customize your gift reminder emails — change the dates, number of reminders, and full control of the text and graphics",
      "Full impact reporting and campaign analytics",
      "‘Pay as you give’ billing — fund a small working balance that tops itself up automatically as gifts are redeemed",
    ],
  },
  pro_plus: {
    name: "Good Pro Plus",
    amountLabel: "$275",
    unit: "/month",
    billingLabel: "Billed annually at $3,300",
    badgeLabel: "Pro Plus",
    features: [
      { divider: "Everything in Pro, plus" },
      "Unlimited users",
      "Organize unlimited users by department",
      "Assign individual budgets to different departments",
      "Allow departments to track their own campaigns and impact",
      "Access to our branded merch hub — use gift credits to pay for bulk orders of branded merch or custom gift boxes (MOQs apply)",
      "Unlimited campaign recipients",
      "Flexible billing for bulk credit purchases",
    ],
  },
  enterprise: {
    name: "Good Enterprise",
    amountLabel: "Custom pricing",
    unit: "",
    billingLabel: "Billed per contract",
    badgeLabel: "Enterprise",
    features: [
      { divider: "Everything in Pro Plus, plus any of" },
      "Dedicated gifting expert",
      "Priority same-day phone support",
      "Quarterly business reviews",
      "Campaign planning & branding assistance",
      "Dedicated onboarding team",
      "Leadership advisory sessions",
      "Budget oversight & spend reports",
      "Custom branded gift sets",
      "Branded merch, full decoration",
      "Fulfillment, storage, branding & kitting",
      "Custom packaging & product sourcing",
      "Custom international delivery logistics",
      "First access to new APIs & integrations",
      "Custom APIs & integrations",
      "Custom engineering & landing pages",
      "Custom unboxing video",
      "Custom order & shipping emails",
      "Custom credit expiration & close-out",
      "Custom impact marketing kit",
      "Annual press release & marketing support",
      "Gift a Pro subscription",
      "Flexible enterprise billing: Net terms and one annual contract price",
    ],
  },
};

/** One plan's display copy. */
export function planDisplay(key: PlanKey): PlanDisplay {
  return PLAN_DISPLAY[key];
}
